import { wrap } from "@reatom/core"
import {
  type GachaApiOptions,
  fetchGachaJson,
  postGachaJson,
} from "./gacha-api"
import {
  type GachaNftApi,
  formatUsd,
  mapOpenPackResult,
  normalizeUsdAmount,
} from "./gacha-data"
import type { GachaExecution, GachaPullResultView } from "./gacha-execution"
import { shouldRequireInstantBuybackForLowCommonStock } from "./gacha-purchase-dialog"
import { getStoredGachaTracking } from "./gacha-tracking"

type GeneratePackResponse = {
  memo: string
  transaction: string
}

type SubmitTransactionResponse = {
  success: boolean
  signature: string
  confirmationStatus: "confirmed" | "finalized" | "submitted"
}

type GachaTransactionContext = {
  type:
    | "PACK_PURCHASE"
    | "GIFT_PACK"
    | "PACK_OPEN"
    | "GIFT_OPEN"
    | "ITEM_BUYBACK"
  privyId: string
  walletAddress: string
  senderWalletAddress?: string | null
  giftRecipientAddress?: string | null
  campaignId?: string | null
  referralCode?: string | null
  machineId: string
  machinePrice?: number | null
  nftAddress?: string | null
  itemName?: string | null
  rarity?: string | null
  itemFMV?: number | null
  buybackAmount?: number | null
}

type OpenPackResponse = {
  success: boolean
  code?: string
  memo?: string
  transactionSignature?: string
  nft_address?: string
  nftWon?: GachaNftApi
  points?: number
  roll?: number
  rarity?: string
  buybackAmount?: number | string
}

type BuybackResponse = {
  success: boolean
  serializedTransaction: string
  refundAmount: number
  memo: string
}

type BuybackCheckResponse = {
  exists?: boolean
  status?: string
  message?: string
}

const TURBO_BUYBACK_CODE = "TURBO_MODE_BUYBACK"
const MULTI_PACK_SIGNATURE_RETRY_LIMIT = 3

class GachaPackSignatureCancelledError extends Error {
  constructor() {
    super("Purchase cancelled.")
    this.name = "GachaPackSignatureCancelledError"
  }
}

function base64ToBytes(value: string) {
  const binary = globalThis.atob(value)
  const bytes = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i)
  }
  return bytes
}

function bytesToBase64(bytes: Uint8Array) {
  let binary = ""
  const chunkSize = 0x8000
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize))
  }
  return globalThis.btoa(binary)
}

async function waitForOpenPack(
  memo: string,
  apiOptions: GachaApiOptions & { gachaTransaction?: GachaTransactionContext },
) {
  for (let attempt = 0; attempt < 8; attempt++) {
    const { gachaTransaction, ...restApiOptions } = apiOptions
    const result = await wrap(
      postGachaJson<OpenPackResponse>(
        "/openPack",
        { memo, ...(gachaTransaction ? { gachaTransaction } : {}) },
        restApiOptions,
      ),
    )
    if (result.code !== "WAITING_FOR_WEBHOOK") {
      return result
    }
    await wrap(new Promise((resolve) => globalThis.setTimeout(resolve, 1500)))
  }

  throw new Error("Pack payment is confirmed but the pack is still opening")
}

export function createSolanaGachaExecution({
  walletAddress,
  signTransaction,
  apiOptions,
  refreshBalance,
  refreshPortfolio,
}: {
  walletAddress: string
  signTransaction: (transaction: Uint8Array) => Promise<Uint8Array>
  apiOptions: GachaApiOptions
  refreshBalance: (walletAddress: string, delta?: number) => void
  refreshPortfolio: () => void
}): GachaExecution & Required<Pick<GachaExecution, "buyback">> {
  const signAndSubmitTransaction = async (
    transactionBase64: string,
    gachaTransaction?: GachaTransactionContext,
  ) => {
    const signedTransaction = await wrap(
      signTransaction(base64ToBytes(transactionBase64)),
    )
    return await wrap(
      postGachaJson<SubmitTransactionResponse>(
        "/submitTransaction",
        {
          signedTransaction: bytesToBase64(signedTransaction),
          ...(gachaTransaction ? { gachaTransaction } : {}),
        },
        apiOptions,
      ),
    )
  }

  return {
    walletAddress,
    validatePurchase({ machine, quantity, instantBuyback }) {
      if (
        shouldRequireInstantBuybackForLowCommonStock({
          machineCode: machine.code,
          commonStock: machine.commonStock,
          purchaseQuantity: quantity,
          instantBuybackEnabled: instantBuyback && machine.turboMode,
        })
      ) {
        throw new Error(
          "Cards are running low. This pack is available in Instant Buyback mode only.",
        )
      }
    },
    async purchase({ machine, quantity, instantBuyback, privyId, onProgress }) {
      const tracking = getStoredGachaTracking()
      const turbo = instantBuyback && machine.turboMode
      const results: GachaPullResultView[] = []
      let turboBuybacks = 0
      let turboBuybackTotal = 0
      let completedPacks = 0
      let cancelled = false

      for (let index = 0; index < quantity; index++) {
        if (quantity > 1) onProgress(`Creating pack ${index + 1}/${quantity}`)
        const pack = await wrap(
          postGachaJson<GeneratePackResponse>(
            "/generatePack",
            { playerAddress: walletAddress, packType: machine.code, turbo },
            apiOptions,
          ),
        )
        onProgress(
          quantity > 1 ? `Sign ${index + 1}/${quantity}` : "Sign in wallet",
        )
        let rejectedAttempts = 0
        try {
          while (true) {
            try {
              await wrap(
                signAndSubmitTransaction(pack.transaction, {
                  type: "PACK_PURCHASE",
                  privyId,
                  walletAddress,
                  campaignId: tracking.campaignId,
                  referralCode: tracking.referralCode,
                  machineId: machine.code,
                  machinePrice: machine.price,
                }),
              )
              refreshBalance(
                walletAddress,
                Number.isFinite(machine.price) ? -machine.price : undefined,
              )
              break
            } catch (caught) {
              const message =
                caught instanceof Error
                  ? caught.message
                  : typeof caught === "string"
                    ? caught
                    : ""
              if (quantity <= 1 || !isWalletCancellationMessage(message))
                throw caught
              rejectedAttempts += 1
              if (rejectedAttempts >= MULTI_PACK_SIGNATURE_RETRY_LIMIT)
                throw new GachaPackSignatureCancelledError()
              onProgress(`Sign ${index + 1}/${quantity} again`)
            }
          }
        } catch (caught) {
          if (
            caught instanceof GachaPackSignatureCancelledError &&
            completedPacks > 0
          ) {
            cancelled = true
            break
          }
          throw caught
        }
        onProgress(
          quantity > 1
            ? `Opening pack ${index + 1} of ${quantity}`
            : "Opening pack",
        )
        const opened = await wrap(
          waitForOpenPack(pack.memo, {
            ...apiOptions,
            gachaTransaction: {
              type: "PACK_OPEN",
              privyId,
              walletAddress,
              campaignId: tracking.campaignId,
              referralCode: tracking.referralCode,
              machineId: machine.code,
              machinePrice: machine.price,
            },
          }),
        )
        const result = mapOpenPackResult(
          { ...opened, memo: pack.memo },
          machine,
        )
        if (
          turbo &&
          opened.code === TURBO_BUYBACK_CODE &&
          opened.buybackAmount !== undefined &&
          opened.rarity?.toLowerCase() === "common"
        ) {
          turboBuybacks += 1
          turboBuybackTotal += normalizeUsdAmount(opened.buybackAmount)
          results.push({
            ...result,
            boughtBackLabel: formatUsd(
              normalizeUsdAmount(opened.buybackAmount),
            ),
          })
        } else {
          results.push(result)
        }
        completedPacks += 1
      }
      if (turboBuybackTotal > 0)
        refreshBalance(walletAddress, turboBuybackTotal)
      refreshPortfolio()
      return {
        results,
        completedPacks,
        cancelled,
        turboBuybacks,
        turboBuybackTotal,
      }
    },
    async buyback({ result, privyId }) {
      if (!result.nftAddress || !result.memo)
        throw new Error("This item is not eligible for buyback.")
      const tracking = getStoredGachaTracking()
      const check = await wrap(
        fetchGachaJson<BuybackCheckResponse>(
          `/buyback/check?memo=${encodeURIComponent(result.memo)}`,
          undefined,
          apiOptions,
        ),
      )
      if (check.exists && check.status === "complete")
        throw new Error("This item has already been bought back.")
      const buyback = await wrap(
        postGachaJson<BuybackResponse>(
          "/buyback",
          { playerAddress: walletAddress, nftAddress: result.nftAddress },
          apiOptions,
        ),
      )
      await wrap(
        signAndSubmitTransaction(buyback.serializedTransaction, {
          type: "ITEM_BUYBACK",
          privyId,
          walletAddress,
          campaignId: tracking.campaignId,
          referralCode: tracking.referralCode,
          machineId: result.machineId,
          nftAddress: result.nftAddress,
          itemName: result.itemName,
          rarity: result.rarity,
          itemFMV: result.itemFMV,
          buybackAmount: normalizeUsdAmount(buyback.refundAmount),
        }),
      )
      for (let attempt = 0; attempt < 8; attempt++) {
        const data = await wrap(
          fetchGachaJson<BuybackCheckResponse>(
            `/buyback/check?memo=${encodeURIComponent(buyback.memo)}`,
            undefined,
            apiOptions,
          ),
        )
        if (data?.exists && data?.status === "complete") {
          refreshBalance(
            walletAddress,
            normalizeUsdAmount(buyback.refundAmount),
          )
          return formatUsd(normalizeUsdAmount(buyback.refundAmount))
        }
        await wrap(
          new Promise((resolve) => globalThis.setTimeout(resolve, 1500)),
        )
      }
      return null
    },
  }
}

// Purchase policy only. Navbar and portfolio intentionally keep their own policy.
export function getPreferredSolanaPurchaseWallet<
  T extends { standardWallet: { name: string } },
>(wallets: readonly T[]): T | undefined {
  const browserWallet = wallets.find(
    (wallet) =>
      "connectorType" in wallet && wallet.connectorType === "solana_adapter",
  )
  if (browserWallet) return browserWallet
  const nonPrivyWallet = wallets.find(
    (wallet) => wallet.standardWallet.name !== "Privy",
  )
  if (nonPrivyWallet) return nonPrivyWallet
  return (
    wallets.find((wallet) => wallet.standardWallet.name === "Privy") ??
    wallets[0]
  )
}

function isWalletCancellationMessage(message: string) {
  const normalized = message.toLowerCase()
  return (
    normalized.includes("user rejected") ||
    normalized.includes("user denied") ||
    normalized.includes("rejected the request") ||
    normalized.includes("cancelled") ||
    normalized.includes("canceled")
  )
}
